import {ActionTree, ActionContext} from 'vuex'
import {IEmployeesStateModel, IRootStateModel} from '../../../interfaces'
import { EmployeesMutations } from './../mutations'
import { ActionTypes } from './types'
import { MutationTypes } from './../mutations/types'
import {
    CompanyEmployeesListResponseModel, 
    CompanyEmployeesListFilterModel,
    DeleteEmployeesListRequestModel,
    ChangeEmployeeStatusModel,
    EditEmployeeRequestModel,
    GetCompanyEmployeeResponseModel,
    CreateEmployeeRequestModel,
    CompanyEmployeeModel,
    TransferCompanyOwnershipRequestModel,
    ChangeEmployeePasswordModel
} from './../../../../services/types'
import { BaseClient } from './../../../../services/client/BaseClient'
import { getClientInstance } from './../../../..//services/client/clientProvider'

type AugmentedActionEmployeesContext = {
    commit<K extends keyof EmployeesMutations>(
      key: K,
      payload: Parameters<EmployeesMutations[K]>[1]
    ): ReturnType<EmployeesMutations[K]>;
} & Omit<ActionContext<IEmployeesStateModel, IRootStateModel>, 'commit'>

export interface EmployeesActions {
    [ActionTypes.SET_EMPLOYEES_LIST](context: AugmentedActionEmployeesContext): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_LIST_FROM_ARRAY](context: AugmentedActionEmployeesContext, list: CompanyEmployeeModel []): void;
    [ActionTypes.SET_EMPLOYEES_LIST_FILTER](context: AugmentedActionEmployeesContext, sort: CompanyEmployeesListFilterModel): void;
    [ActionTypes.SET_EMPLOYEES_EMPLOYEE_CHANGE_STATUS](context: AugmentedActionEmployeesContext, model: ChangeEmployeeStatusModel): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_EMPLOYEE_DELETE](context: AugmentedActionEmployeesContext, model: DeleteEmployeesListRequestModel): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_EMPLOYEE_EDIT](context: AugmentedActionEmployeesContext, model: EditEmployeeRequestModel): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_EMPLOYEE_CREATE](context: AugmentedActionEmployeesContext, model: CreateEmployeeRequestModel): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_SELECTED_EMPLOYEE](context: AugmentedActionEmployeesContext, id: number): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_TRANSFER_COMPANY_OWNERSHIP](context: AugmentedActionEmployeesContext, model: TransferCompanyOwnershipRequestModel): Promise<void>;
    [ActionTypes.SET_EMPLOYEES_SELECTED_EMPLOYEE_CLEAR](context: AugmentedActionEmployeesContext): void;
    [ActionTypes.SET_EMPLOYEES_ERROR_MESSAGE_CLEAR](context: AugmentedActionEmployeesContext): void;
    [ActionTypes.SET_EMPLOYEES_EMPLOYEE_CHANGE_PASSWORD](context: AugmentedActionEmployeesContext, model: ChangeEmployeePasswordModel): Promise<void>;
}

export const actions: ActionTree<IEmployeesStateModel, IRootStateModel> & EmployeesActions = {
    async [ActionTypes.SET_EMPLOYEES_LIST]({commit, getters}) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        try {
            const response: CompanyEmployeesListResponseModel = await client.getCompanyEmployeesList(getters.getCompaniesSelectedCompany.id) 

            commit(MutationTypes.SET_EMPLOYEES_LIST, response.users)
        }
        catch(error) {
            console.log('getCompanyEmployeesList', error)
        }
        finally {
            commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, false)
        }
    },
    [ActionTypes.SET_EMPLOYEES_LIST_FROM_ARRAY]({commit}, list: CompanyEmployeeModel []) {
        commit(MutationTypes.SET_EMPLOYEES_LIST, list)
    },
    [ActionTypes.SET_EMPLOYEES_LIST_FILTER]({commit}, filter: CompanyEmployeesListFilterModel) {
        commit(MutationTypes.SET_EMPLOYEES_FILTER_VALUE, filter)
    },
    async [ActionTypes.SET_EMPLOYEES_EMPLOYEE_CHANGE_STATUS]({commit, dispatch}, model: ChangeEmployeeStatusModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        try{
            await client.changeEmployeeStatus(model.id, {
                status: model.status == 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',                
            })
        }
        catch(error){
            console.log('changeEmployeeStatus', error)
        }
        finally {
            dispatch(ActionTypes.SET_EMPLOYEES_LIST)
        }
        
    },
    async [ActionTypes.SET_EMPLOYEES_EMPLOYEE_DELETE]({commit, dispatch}, model: DeleteEmployeesListRequestModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        try{
            await client.deleteEmployeesList(model)
        }
        catch(error){
            console.log('deleteEmployeesList', error)
        }
        finally {
            dispatch(ActionTypes.SET_EMPLOYEES_LIST)
        }
    },
    async [ActionTypes.SET_EMPLOYEES_EMPLOYEE_CREATE]({commit, getters}, model: CreateEmployeeRequestModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        const companyId = getters.getCompaniesSelectedCompany.id
        
        try{    
            await client.createEmployee(companyId, model)
        }
        catch(error){
            console.log('createEmployee', error);
            if (error.message) {
                commit(MutationTypes.SET_EMPLOYEES_ERROR_MESSAGE, error.message);
            }            
        }
        finally {
            commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, false)            
        }
    },
    async [ActionTypes.SET_EMPLOYEES_EMPLOYEE_EDIT]({commit, dispatch, getters}, model: EditEmployeeRequestModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        const id = getters.getEmployeesSelectedEmployee.id

        try{
            await client.editEmployee(id, model)
        }
        catch(error){
            console.log('editEmployee', error)
        }
        finally {
            dispatch(ActionTypes.SET_EMPLOYEES_LIST)
        }
    },
    async [ActionTypes.SET_EMPLOYEES_SELECTED_EMPLOYEE]({commit}, id: number) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        try{
            const response: GetCompanyEmployeeResponseModel = await client.getEmployee(id)
            commit(MutationTypes.SET_EMPLOYEES_SELECTED_EMPLOYEE, response)
        }
        catch(error){
            console.log('getEmployee', error)
        }
        finally {
            commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, false)
        }
    },
    async [ActionTypes.SET_EMPLOYEES_TRANSFER_COMPANY_OWNERSHIP]({commit, dispatch, getters}, model: TransferCompanyOwnershipRequestModel) {
        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        const id = getters.getCompaniesSelectedCompany.id

        try{
            await client.transferCompanyOwnership(id, model)
        }
        catch(error){
            console.log('transferCompanyOwnership', error)
        }
        finally {
            dispatch(ActionTypes.SET_EMPLOYEES_LIST)
        }

    },
    [ActionTypes.SET_EMPLOYEES_SELECTED_EMPLOYEE_CLEAR]({commit}) {
        commit(MutationTypes.SET_EMPLOYEES_SELECTED_EMPLOYEE, null)
    },
    [ActionTypes.SET_EMPLOYEES_ERROR_MESSAGE_CLEAR]({commit}) {
        commit(MutationTypes.SET_EMPLOYEES_ERROR_MESSAGE, null)
    },
    async [ActionTypes.SET_EMPLOYEES_EMPLOYEE_CHANGE_PASSWORD]({commit, dispatch}, model: ChangeEmployeePasswordModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_EMPLOYEES_DATA_IS_LOADING, true)

        try{
            await client.changeEmployeePassword(
                model.companyId,
                model.userId, 
                {
                newPassword: model.newPassword
            })
        }
        catch(error){
            console.error('changeEmployeePassword', error)
            throw error
        }
        finally {
            dispatch(ActionTypes.SET_EMPLOYEES_LIST)
        }

    },
}